import {
  AcademicCapIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const NewLP: React.FC = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFAQ = (index: number) => {
    if (selectedFAQ === index) {
      setSelectedFAQ(null);
    } else {
      setSelectedFAQ(index);
    }
  };

  // FAQ data
  const faqs = [
    {
      question: "Eu não tenho tempo para gerenciar minhas finanças. Como o Cofrim pode ajudar?",
      answer: "Entendemos que como uma profissional ocupada, seu tempo é valioso. Por isso, o Cofrim foi projetado para se integrar perfeitamente à sua rotina, com rastreamento automático de transações, categorização inteligente e insights proativos. Você precisa de apenas alguns minutos por dia para acompanhar seu progresso financeiro e tomar decisões informadas. Nosso aplicativo faz o trabalho pesado para você!"
    },
    {
      question: "Já tentei outros aplicativos financeiros e nenhum funcionou para mim. Por que o Cofrim seria diferente?",
      answer: "Muitos de nossos usuários vieram para o Cofrim após experiências frustrantes com outros aplicativos. A diferença está em nosso foco específico nas necessidades de jovens profissionais, com ferramentas personalizadas que se adaptam ao seu estilo de vida. Oferecemos uma combinação única de rastreamento financeiro, planejamento de metas, educação e insights personalizados - tudo em uma interface intuitiva. E você pode experimentar gratuitamente por 7 dias antes de qualquer cobrança."
    },
    {
      question: "Como posso ter certeza de que meus dados financeiros estão seguros?",
      answer: "Sua segurança é nossa prioridade máxima. O Cofrim utiliza criptografia de nível bancário, autenticação de dois fatores e auditorias de segurança regulares. Nunca vendemos seus dados a terceiros e você tem controle total sobre suas informações. Nossos servidores são protegidos por sistemas de segurança avançados e monitoramento 24/7 para garantir que suas informações financeiras permaneçam privadas e seguras."
    },
    {
      question: "Quanto custa o Cofrim e o que está incluído na assinatura?",
      answer: "O Cofrim oferece diferentes planos para atender às suas necessidades. Nossa assinatura Premium inclui acesso ilimitado a todas as ferramentas, incluindo rastreamento financeiro, orçamentos personalizados, planejamento de metas, educação financeira e suporte prioritário. Visite nossa página de preços para ver todos os detalhes e encontrar o plano ideal para você. E lembre-se: vemos o Cofrim não como um custo, mas como um investimento que gera retornos significativos em sua vida financeira."
    }
  ];

  // Feature list for hero section
  const heroFeatures: any[] = []
  // const heroFeatures = [
  //   "Controle financeiro completo e automatizado",
  //   "Orçamentos inteligentes adaptados ao seu estilo de vida",
  //   "Planejamento de metas para realizar seus sonhos",
  //   "Insights personalizados para otimizar seus gastos",
  //   "Educação financeira para construir habilidades duradouras"
  // ];

  // Main features section
  const features = [
    {
      icon: CurrencyDollarIcon,
      title: "Rastreamento Automático de Gastos",
      description: "Tenha visibilidade total sobre suas finanças com categorização automática de transações e relatórios visuais intuitivos. Saiba exatamente para onde seu dinheiro está indo sem esforço manual.",
      image: "/images/placeholder-feature-1.jpg" // Placeholder - you can replace later
    },
    {
      icon: ChartBarIcon,
      title: "Orçamentos Inteligentes & Personalizados",
      description: "Nossos orçamentos adaptáveis são baseados em seus gastos reais e objetivos de vida. Eles se ajustam automaticamente ao seu comportamento, tornando fácil manter-se no caminho certo.",
      image: "/images/placeholder-feature-2.jpg" // Placeholder - you can replace later
    },
    {
      icon: SparklesIcon,
      title: "Planejamento de Metas Impulsionado por IA",
      description: "Transforme seus sonhos em planos concretos. Nossa tecnologia avançada cria roteiros personalizados para suas metas financeiras, com acompanhamento visual e ajustes inteligentes.",
      image: "/images/placeholder-feature-3.jpg" // Placeholder - you can replace later
    },
    {
      icon: AcademicCapIcon,
      title: "Academia Financeira Integrada",
      description: "Aprenda enquanto faz. Nossa biblioteca de conteúdo educacional se integra perfeitamente à sua jornada financeira, oferecendo dicas personalizadas exatamente quando você precisa delas.",
      image: "/images/placeholder-feature-4.jpg" // Placeholder - you can replace later
    }
  ];

  // Pricing plans
  const plans = [
    {
      name: "Básico",
      price: "R$0",
      period: "para sempre",
      description: "Perfeito para começar sua jornada financeira",
      features: [
        "Rastreamento de despesas básico",
        "Orçamentos simples",
        "Metas financeiras (limite de 2)",
        "Acesso a conteúdo educacional básico",
        "Suporte por email"
      ],
      cta: "Testar 7 dias Grátis",
      most_popular: false
    },
    {
      name: "Premium",
      price: "R$29,90",
      period: "por mês",
      description: "Ideal para quem quer controle financeiro completo",
      features: [
        "Rastreamento de despesas automático e ilimitado",
        "Orçamentos inteligentes personalizados",
        "Metas financeiras ilimitadas",
        "Análises e insights personalizados",
        "Educação financeira completa",
        "Suporte prioritário 24/7",
        "7 dias grátis"
      ],
      cta: "Assinar Agora",
      most_popular: true
    },
    {
      name: "Família",
      price: "R$49,90",
      period: "por mês",
      description: "Para gerenciar finanças em família",
      features: [
        "Tudo do plano Premium",
        "Até 5 usuários",
        "Painel financeiro familiar",
        "Metas compartilhadas",
        "Controles de privacidade avançados",
        "Consultoria financeira trimestral",
        "Ferramentas de planejamento de herança"
      ],
      cta: "Escolher Plano Família",
      most_popular: false
    }
  ];

  return (
    <div className="overflow-hidden bg-white">
      {/* Navbar */}
      <header className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img
                className="h-10 w-auto"
                src={isScrolled ? "/images/logo-blue.png" : "/images/logo.png"} // You can replace this
                alt="Cofrim Logo"
              />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#start" onClick={(e) => { e.preventDefault(); scrollToSection('start'); }} className={`text-sm font-medium ${isScrolled ? 'text-gray-800 hover:text-indigo-600' : 'text-white hover:text-indigo-200'} transition-colors`}>
                Inicio
              </a>
              <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }} className={`text-sm font-medium ${isScrolled ? 'text-gray-800 hover:text-indigo-600' : 'text-white hover:text-indigo-200'} transition-colors`}>
                Como Funciona
              </a>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/login")}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${isScrolled ? 'text-indigo-600 hover:text-indigo-800' : 'text-white hover:text-indigo-200'}`}
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-6 py-2.5 text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 hover:scale-105"
              >
                Teste grátis por 7 dias
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div id="start" className="relative pt-16 pb-24 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-900 to-indigo-600 transform -skew-y-6 origin-top-left"></div>

        {/* Background pattern */}
        <div className="absolute inset-0 bg-repeat bg-opacity-10 mix-blend-overlay"></div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 sm:pt-32 sm:pb-20">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div className="mb-12 lg:mb-0">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                Transforme Sua Relação com o Dinheiro
              </span>
              <h1 className="mt-4 text-2xl sm:text-2xl lg:text-5xl font-extrabold text-white tracking-tight">
                Domine suas finanças e construa o futuro dos seus sonhos
              </h1>
              <p className="mt-6 text-xl text-indigo-100 max-w-2xl">
                A ferramenta completa para quem quer tomar o controle de suas finanças e realizar seus objetivos de vida.
              </p>

              {/* Feature list with check icons */}
              <div className="mt-8 space-y-4">
                {heroFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-indigo-300 flex-shrink-0" />
                    <span className="ml-3 text-indigo-100">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA buttons */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate("/signup")}
                  className="group px-10 py-4 text-lg font-bold rounded-xl text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus:ring-4 focus:ring-emerald-300 shadow-2xl hover:shadow-emerald-500/25 transition-all duration-500 transform hover:-translate-y-1 hover:scale-105 relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    ✨ Teste grátis por 7 dias
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </button>
                <button
                  onClick={() => scrollToSection('how-it-works')}
                  className="px-8 py-3 text-base font-medium rounded-md text-white bg-indigo-500 bg-opacity-30 hover:bg-opacity-40 border border-indigo-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all"
                >
                  Saiba Como Funciona
                </button>
              </div>
            </div>

            <img src="/images/mockup.png" alt="" />

          </div>
        </div>
      </div>

      {/* Problem Recognition Section */}
      <div className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div className="relative mb-12 lg:mb-0">
              {/* Image placeholder */}
              <div className="flex flex-col gap-4">
                <NewsHighlight title="Dos brasileiros não tem educação financeira adequada" percentage="79%" source="Estudo OCDE/ANBIMA (2023)" />
                <NewsHighlight title="Dos brasileiros não fazem controle do orçamento familiar " percentage="64%" source="Pesquisa CNDL/SPC Brasil (2023)" />
                <NewsHighlight title="É a porcentagem dos brasileiros que conseguem poupar regularmente" percentage="36%" source="Pesquisa Anbima 'Raio X do Investidor Brasileiro' (2023)" />
                <NewsHighlight title="Dos brasileiros possuem uma reserva de emergência adequada" percentage="12%" source="Pesquisa Datafolha/Banco Central (2023)" />
              </div>
            </div>

            <div>
              <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">Você se identifica?</h2>
              <p className="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
                A ansiedade financeira é real e impacta sua vida
              </p>
              <div className="mt-6 text-gray-500 space-y-6">
                <p className="text-lg">
                  Como jovem profissional em ascensão, você trabalha duro para construir a vida dos seus sonhos. Mas apesar do seu sucesso, a insegurança financeira ainda está presente:
                </p>
                <ul className="space-y-4 text-lg">
                  <li className="flex">
                    <div className="flex-shrink-0">
                      <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 text-sm font-bold">✕</span>
                      </div>
                    </div>
                    <p className="ml-3">Você teme não estar poupando o suficiente para seu futuro</p>
                  </li>
                  <li className="flex">
                    <div className="flex-shrink-0">
                      <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 text-sm font-bold">✕</span>
                      </div>
                    </div>
                    <p className="ml-3">A incerteza sobre investimentos gera dúvidas constantes</p>
                  </li>
                  <li className="flex">
                    <div className="flex-shrink-0">
                      <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 text-sm font-bold">✕</span>
                      </div>
                    </div>
                    <p className="ml-3">Você perde o controle sobre seus gastos facilmente</p>
                  </li>
                  <li className="flex">
                    <div className="flex-shrink-0">
                      <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 text-sm font-bold">✕</span>
                      </div>
                    </div>
                    <p className="ml-3">Planilhas e outros aplicativos parecem complicados demais ou simplistas demais</p>
                  </li>
                </ul>
                <p className="text-lg font-medium text-gray-700">
                  Essa ansiedade constante consome sua energia, rouba sua paz de espírito e atrasa o progresso em direção aos seus objetivos. <span className="text-indigo-600 font-semibold">Mas não precisa ser assim.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Solution Section */}
      <div id="how-it-works" className="bg-gradient-to-r from-indigo-800 to-indigo-900 py-16 sm:py-24 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="h-full w-full" fill="none">
            <defs>
              <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M0 0 L40 0 L40 40 L0 40 Z" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-base font-semibold text-indigo-300 tracking-wide uppercase">A Solução</h2>
            <p className="mt-2 text-3xl font-extrabold text-white tracking-tight sm:text-4xl">
              Apresentando o Cofrim
            </p>
            <p className="mt-5 text-xl text-indigo-100">
              Seu parceiro personalizado para prosperidade financeira a longo prazo, com ferramentas intuitivas, aulas e insights inteligentes que transformam sua relação com o dinheiro.
            </p>
          </div>

          <iframe
            id={`cofrim-apresentacao`}
            src={`https://www.youtube.com/embed/Kg6jChyMoZo?autoplay=1&loop=1&playlist=Kg6jChyMoZo`}
            title={"Cofrim"}
            allowFullScreen
            // allow="autoplay"
            className="w-full aspect-video rounded-lg shadow-lg border-primaryBorder border"
          ></iframe>

          {/* How it works steps */}
          <div className="mt-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-xl p-8 border border-white border-opacity-20 transform hover:-translate-y-1 transition-all duration-300">
                <div className="h-12 w-12 rounded-full bg-indigo-500 flex items-center justify-center mb-6">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Importe seus dados</h3>
                <p className="text-indigo-100">
                  Defina seu orçamento, cadastre suas despesas fixas, anote suas receitas e despesas, tudo isso através de uma interfáce simples e intuitiva.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-xl p-8 border border-white border-opacity-20 transform hover:-translate-y-1 transition-all duration-300">
                <div className="h-12 w-12 rounded-full bg-indigo-500 flex items-center justify-center mb-6">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Defina Seus Objetivos</h3>
                <p className="text-indigo-100">
                  Estabeleça metas financeiras claras que se alinham com seus valores e sonhos. Adicione todas suas metas de curto, medio e longo prazo, e acompanhe a evolução de cada uma delas.
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-xl p-8 border border-white border-opacity-20 transform hover:-translate-y-1 transition-all duration-300">
                <div className="h-12 w-12 rounded-full bg-indigo-500 flex items-center justify-center mb-6">
                  <span className="text-white font-bold text-lg">3</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Aprendizado constante</h3>
                <p className="text-indigo-100">
                  Assista aulas exclusivas sobre educação financeira, investimentos e extraia o máximo da plataforma e do controle sobre sua vida financeira.
                </p>
              </div>
            </div>

            {/* Results */}
            {/* <div className="mt-16 text-center">
              <p className="text-2xl font-bold text-white mb-8">Por que começar hoje mesmo?</p> */}
            {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-indigo-700 rounded-lg p-6 transform hover:scale-105 transition-all">
                  <p className="text-4xl font-bold text-white mb-2">98%</p>
                  <p className="text-indigo-200">Redução na ansiedade financeira</p>
                </div>
                <div className="bg-indigo-700 rounded-lg p-6 transform hover:scale-105 transition-all">
                  <p className="text-4xl font-bold text-white mb-2">3x</p>
                  <p className="text-indigo-200">Aumento na taxa de poupança</p>
                </div>
                <div className="bg-indigo-700 rounded-lg p-6 transform hover:scale-105 transition-all">
                  <p className="text-4xl font-bold text-white mb-2">87%</p>
                  <p className="text-indigo-200">Alcançam suas metas financeiras</p>
                </div>
                <div className="bg-indigo-700 rounded-lg p-6 transform hover:scale-105 transition-all">
                  <p className="text-4xl font-bold text-white mb-2">5+</p>
                  <p className="text-indigo-200">Horas economizadas por mês</p>
                </div>
              </div> */}
            {/* </div> */}
          </div>
        </div>
      </div>
      <div className="mt-16 text-center">
        <p className="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl mb-8">Por que começar hoje mesmo?</p>
        <div className="flex flex-row flex-wrap items-center justify-center gap-4">
          <NewsHighlight type="success" title="Das pessoas que mantêm um orçamento estruturado relatam menor estresse financeiro" percentage="84%" source="Relatório Global de Bem-estar Financeiro - MetLife (2023)" />
          <NewsHighlight type="success" title="Mais chances de poupar regularmente, tem uma pessoa que usa app de controle financeiro" percentage="53%" source="Pesquisa Febraban (2023)" />
          <NewsHighlight type="success" title="É a media de diminuição dos gastos impulsivos usando uma ferramenta de gestão financeira" percentage="31%" source="Estudo FGV sobre Comportamento Financeiro (2023)" />
          <NewsHighlight type="success" title="É a economia média por ano das pessoas que controlam suas finanças" percentage="22%" source="Estudo Banco Central - Cidadania Financeira (2023)" />
        </div>
      </div>

      {/* Features Section */}
      {/* <NewLPFeatures /> */}

      {/* Pricing Section */}
      <div id="pricing" className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">COMECE AGORA MESMO</h2>
            <p className="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
              Comece seu teste grátis por 7 dias
            </p>
            <p className="mt-5 max-w-xl mx-auto text-xl text-gray-500">
              <button
                onClick={() => navigate("/signup")}
                className="group px-10 py-4 text-xl font-bold bg-gradient-to-r from-indigo-600 to-blue-700 text-white rounded-2xl hover:from-indigo-700 hover:to-blue-800 transition-all duration-500 shadow-2xl hover:shadow-indigo-500/30 transform hover:-translate-y-2 hover:scale-110 relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  🚀 Começar agora
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-700 to-blue-700 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-500"></div>
              </button>
            </p>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="bg-indigo-900">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <img
                className="h-10 w-auto"
                src="/images/logo.png" // You can replace this
                alt="Cofrim Logo"
              />
              <p className="mt-4 text-indigo-200 text-sm">
                Sua parceria para prosperidade financeira e paz de espírito.
              </p>
              <div className="mt-6 flex space-x-6">
                <a href="https://instagram.com/meucofrim" target='_blank' className="text-indigo-300 hover:text-indigo-100">
                  <span className="sr-only">Instagram</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="https://www.youtube.com/@AppCofrim" target='_blank' className="text-indigo-300 hover:text-indigo-100">
                  <span className="sr-only">YouTube</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-indigo-300 tracking-wider uppercase">Contato</h3>
              <div className="mt-4">
                <p className="text-base text-indigo-200">
                  Para dúvidas, suporte ou mais informações:
                </p>
                <a href="mailto:contato@cofrim.com.br" className="text-lg text-indigo-100 hover:text-white font-medium mt-2 block transition-colors">
                  contato@cofrim.com.br
                </a>
                <div className="mt-6 space-y-2">
                  <button
                    onClick={() => navigate("/politica-de-privacidade")}
                    className="text-sm text-indigo-200 hover:text-indigo-100 transition-colors block"
                  >
                    Política de Privacidade
                  </button>
                  <button
                    onClick={() => navigate("/termos-de-uso")}
                    className="text-sm text-indigo-200 hover:text-indigo-100 transition-colors block"
                  >
                    Termos de Uso
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 border-t border-indigo-700 pt-8">
            <p className="text-base text-indigo-200 text-center">
              &copy; {new Date().getFullYear()} Cofrim. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

type NewsHighlightProps = {
  title: string;
  percentage: string;
  source: string;
  type?: "error" | "success";
}

function NewsHighlight({ title, percentage, source, type = "error" }: NewsHighlightProps) {
  const isErrorType = type === "error";
  return <div className={`flex flex-grow p-4 items-center justify-start rounded-md max-w-full md:max-w-lg ${isErrorType ? "bg-red-950/80" : "bg-green-950/80"} flex-row gap-4`}>
    <div className={`flex items-center justify-center max-w-20 min-w-20 w-20 h-16 rounded-lg ${isErrorType ? "bg-red-700" : "bg-green-700"} p-2 text-2xl text-white`}>
      {percentage}
    </div>
    <div className="flex flex-col justify-start items-start text-left gap-1 text-white text-md">
      <p>{title}</p>
      <div className="flex flex-row gap-1 text-xs items-center">
        Fonte:
        <p className="text-xs underline">{source}</p></div>

    </div>

  </div>
}

export default NewLP;
